import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  Text,
  useToast,
  Container,
  Heading,
  Flex,
} from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LOGO_URL, APP_NAME, PAGE_TITLES } from '../constants/branding';
import { getMsalService } from '../services/microsoftAuthService';
import { validateMsalConfig } from '../config/msal';

const Login = () => {
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMicrosoftEnabled, setIsMicrosoftEnabled] = useState(false);

  const { isAuthenticated } = useAuth();

  // Set document title and check Microsoft auth config when component mounts
  useEffect(() => {
    document.title = PAGE_TITLES.LOGIN;
    
    // Check if Microsoft authentication is configured
    const msEnabled = validateMsalConfig();
    setIsMicrosoftEnabled(msEnabled);
    
    if (!msEnabled) {
      toast({
        title: 'Configuration Error',
        description: 'Microsoft authentication is not configured. Please contact your administrator.',
        status: 'error',
        duration: 10000,
        isClosable: true,
      });
    }
    
    // Check for Microsoft auth error (set by pre-React handler)
    const msalError = sessionStorage.getItem('msal_auth_error');
    if (msalError) {
      toast({
        title: 'Microsoft login failed',
        description: msalError,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      sessionStorage.removeItem('msal_auth_error');
    }
    
    // Check if there's an error from Microsoft auth redirect
    if (location.state?.error) {
      toast({
        title: 'Microsoft login failed',
        description: location.state.error,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      // Clear the error from location state
      window.history.replaceState({}, document.title);
    }

    // NOTE: No need to manually call authLogin() here
    // AuthContext automatically picks up tokens from localStorage on initialization
  }, [location, toast]);

  // Redirect to home if user becomes authenticated
  // This handles the case after Microsoft SSO completes
  useEffect(() => {
    console.log('[Login] isAuthenticated changed:', isAuthenticated);
    if (isAuthenticated) {
      console.log('[Login] User is authenticated, redirecting to home...');
      // Use a small delay to ensure state is fully updated
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 100);
    }
  }, [isAuthenticated, navigate]);

  const handleMicrosoftLogin = async () => {
    // Prevent multiple clicks
    if (isMicrosoftLoading) {
      console.log('[Login] Already loading, preventing duplicate click');
      return;
    }
    
    // Check if we're already in the middle of authentication
    const isAuthInProgress = sessionStorage.getItem('msal_redirect_processed');
    if (isAuthInProgress) {
      console.log('[Login] Microsoft authentication already in progress');
      return;
    }
    
    setIsMicrosoftLoading(true);
    console.log('[Login] Initiating Microsoft login redirect...');
    
    try {
      // Get the MSAL service (should be initialized by now from index.tsx)
      const msalService = getMsalService();
      
      // Mark that we're starting Microsoft login
      sessionStorage.setItem('msal_login_initiated', 'true');
      
      console.log('[Login] Redirecting to Microsoft...');
      
      // Redirect to Microsoft login
      // This will navigate away from the page
      await msalService.loginRedirect();
      
      // Note: Code after this won't execute because page redirects
    } catch (error: any) {
      console.error('[Login] Microsoft login error:', error);
      
      // Clear the initiated flag
      sessionStorage.removeItem('msal_login_initiated');
      
      // If it's an interaction_in_progress error, give specific advice
      let errorMessage = error.message || 'Failed to initiate Microsoft login';
      if (errorMessage.includes('interaction_in_progress')) {
        errorMessage = 'Please clear your browser cache and try again.';
      }
      
      // Show error to user
      toast({
        title: 'Microsoft login failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
      });
      
      setIsMicrosoftLoading(false);
    }
  };

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg="gray.50"
      position="relative"
      overflow="hidden"
    >
      {/* Background Design */}
      <Box
        position="absolute"
        top="-10%"
        right="-10%"
        width="600px"
        height="600px"
        bg="blue.500"
        borderRadius="full"
        opacity={0.1}
      />
      <Box
        position="absolute"
        bottom="-15%"
        left="-15%"
        width="800px"
        height="800px"
        bg="blue.400"
        borderRadius="full"
        opacity={0.1}
      />

      <Container maxW="sm" position="relative" zIndex={1}>
        <VStack spacing={8} bg="white" p={10} borderRadius="xl" boxShadow="xl">
          {/* Logo and Title */}
          <VStack spacing={3} w="full" align="center">
            <Box mb={2}>
              <img 
                src={LOGO_URL}
                alt="IT360 Logo" 
                style={{ height: '60px', width: 'auto' }} 
              />
            </Box>
            <Heading size="xl" color="blue.600">Welcome Back</Heading>
            <Text color="gray.600" fontSize="lg" textAlign="center">
              Sign in to {APP_NAME} with your Microsoft account
            </Text>
          </VStack>

          {/* Microsoft Login Button */}
          <Button
            onClick={handleMicrosoftLogin}
            size="lg"
            width="100%"
            isLoading={isMicrosoftLoading}
            isDisabled={!isMicrosoftEnabled}
            loadingText="Redirecting to Microsoft..."
            py={6}
            fontSize="md"
            bg="white"
            border="2px solid"
            borderColor="gray.300"
            color="gray.700"
            leftIcon={
              <svg width="20" height="20" viewBox="0 0 21 21" fill="none">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
            }
            _hover={{
              bg: 'gray.50',
              transform: 'translateY(-2px)',
              boxShadow: 'lg',
              borderColor: 'blue.400',
            }}
            _active={{
              bg: 'gray.100',
            }}
            transition="all 0.2s"
          >
            Sign in with Microsoft
          </Button>

          {!isMicrosoftEnabled && (
            <Text fontSize="sm" color="red.500" textAlign="center">
              Microsoft authentication is not configured
            </Text>
          )}

          {/* Info Text */}
          <VStack spacing={2} pt={4}>
            <Text fontSize="sm" color="gray.500" textAlign="center">
              Secure authentication powered by Microsoft Azure AD
            </Text>
            <Text fontSize="xs" color="gray.400" textAlign="center">
              Your credentials are never stored on our servers
            </Text>
          </VStack>
        </VStack>
      </Container>
    </Flex>
  );
};

export default Login; 